import React, { useCallback, useEffect } from 'react';

import { parseISO } from 'date-fns';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { createErrorBanner } from 'components/Banner';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import { Table } from 'components/Table';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import VenueSelector from 'components/VenueSelector';
import { ICONS } from 'constants/icons';
import { Title } from 'features/App';
import { bem } from 'helpers/class-name';
import namedFormats from 'helpers/intl-named-formats';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { selectActiveVenue } from 'selectors/general';

import { PAGE_SIZE } from './SkuPayoutReports.constants';
import { skuPayoutReportsMessages as messages } from './SkuPayoutReports.messages';
import useSkuPayoutsList from './useSkuPayoutsList';
import './SkuPayoutReports.scss';

const { block, element } = bem('sku-payout-reports');

const HeaderRow = () => (
  <tr>
    <Message as="th" message={messages.columnPayoutId} />
    <Message as="th" message={messages.columnPayoutPeriod} />
    <Message as="th" message={messages.columnDatePosted} />
    <th className="text--right">
      <Message message={messages.columnTotalPayout} />
      <TooltipToggle name="sku-payout-list-total-payout" className="sku-payout-reports__info-icon">
        <Isvg src={ICONS.infoGrey} />
      </TooltipToggle>
      <TooltipContent
        name="sku-payout-list-total-payout"
        place="top"
        offset={{ top: 7 }}
        className="sku-payout-reports__tooltip-content">
        <Message message={messages.listColumnTotalPayoutTooltip} />
      </TooltipContent>
    </th>
    <th aria-hidden />
  </tr>
);

const SkuPayoutReportsListDesktop = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { venueId: activeVenueId } = useSelector(selectActiveVenue);

  const onLoadError = useCallback(() => {
    dispatch(createErrorBanner(messages.listLoadError));
  }, [dispatch]);

  const { payouts, count, page, changePage, isLoading } = useSkuPayoutsList({
    venueId: activeVenueId,
    onLoadError,
  });

  useEffect(() => {
    if (activeVenueId) {
      Segment.event('Payout Report List Viewed', { venueId: activeVenueId });
    }
  }, [activeVenueId]);

  const handleRowClick = payoutId => () => {
    navigate(makePath.skuPayoutReportDetail(payoutId));
  };

  const formatDate = date => (date ? intl.formatDate(parseISO(date), namedFormats.date.short) : '');

  return (
    <div className={block()}>
      <Title>
        <Message message={messages.listPageTitle} />
      </Title>
      <Table
        hasFilters
        filters={<VenueSelector />}
        hasBottomPageTurner={count > PAGE_SIZE}
        entriesPerPage={PAGE_SIZE}
        count={count}
        page={page}
        changePage={changePage}
        isLoading={isLoading}
        emptyMessage={messages.listEmpty}
        header={<HeaderRow />}>
        {payouts.map(
          ({
            payoutId,
            payoutAsOfDate,
            previousPayoutAsOfDate,
            payoutSubmissionDate,
            currencyCode,
            totalVenuePayoutConverted,
          }) => (
            <tr key={payoutId} className={element('row')} onClick={handleRowClick(payoutId)}>
              <td className={element('payout-id-cell')}>{payoutId}</td>
              <td>{[formatDate(previousPayoutAsOfDate), formatDate(payoutAsOfDate)].join(' – ')}</td>
              <td>{formatDate(payoutSubmissionDate)}</td>
              <td className="text--right">
                <Message format="currency" currency={currencyCode} value={totalVenuePayoutConverted ?? 0} />
              </td>
              <td className={element('chevron-cell')}>
                <SvgArrow direction="right" variant="chevron" theme="dark" />
              </td>
            </tr>
          )
        )}
      </Table>
    </div>
  );
};

export default SkuPayoutReportsListDesktop;
